import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Chrome,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/common/Logo';
import './Auth.css';

const Signup: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    subscribeNewsletter: true
  });

  const { state, signup, loginWithGoogle, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (state.isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [state.isAuthenticated, navigate, location]);

  // Don't clear errors on mount - let them persist until user interacts
  // Errors will be cleared when user starts typing (in handleInputChange)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (state.error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      return; // Form validation will show error
    }

    // Don't clear error here - let AuthContext handle it
    // The error will be cleared by AUTH_START, then set by AUTH_FAILURE if it fails
    await signup({
      fullName: formData.fullName,
      email: formData.email,
      password: formData.password
    });
  };

  const handleGoogleSignup = async () => {
    await loginWithGoogle();
  };

  const passwordRequirements = [
    { text: 'At least 8 characters', met: formData.password.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(formData.password) },
    { text: 'Contains number', met: /\d/.test(formData.password) }
  ];

  return (
    <div className="auth-page fixed inset-0 overflow-y-auto overscroll-contain">
      <div className="auth-background" aria-hidden="true">
        <div className="auth-background__grid" />
        <div className="auth-background__glow auth-background__glow--top" />
        <div className="auth-background__glow auth-background__glow--bottom" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20">
          <div className="auth-header flex items-center justify-between">
            <Logo size="lg" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/login');
              }}
              className="auth-header__action"
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="auth-main auth-main--signup flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            {/* Signup Form */}
            <div className="auth-card auth-card--signup">
              <div className="auth-card__intro">
                <span className="auth-card__eyebrow">Create your account</span>
                <h1>Build interview confidence</h1>
                <p>Save your learning progress.</p>
              </div>

              {/* Error Message */}
              {state.error && (
                <div className="auth-alert">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <p className="text-orange-200 text-sm">{state.error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3" name="signup" autoComplete="on">
                {/* Full Name Field */}
                <div>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    autoComplete="name"
                    className="auth-input"
                    placeholder="Full name"
                    required
                  />
                </div>

                {/* Email Field */}
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="email"
                    className="auth-input"
                    placeholder="Email"
                    required
                  />
                </div>

                {/* Password Field */}
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className="auth-input auth-input--password"
                    placeholder="Create password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="auth-password-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password Requirements */}
                {formData.password && (
                  <div className="auth-requirements">
                    {passwordRequirements.map((req, index) => (
                      <div key={index} className="flex items-center space-x-1.5">
                        <CheckCircle
                          className={`w-3.5 h-3.5 flex-shrink-0 ${req.met ? 'text-green-400' : 'text-gray-500'}`}
                        />
                        <span className={`text-xs ${req.met ? 'text-green-400' : 'text-gray-500'}`}>
                          {req.text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Confirm Password Field */}
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    className="auth-input auth-input--password"
                    placeholder="Confirm password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="auth-password-toggle"
                    aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password Match Validation */}
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="auth-validation-error">Passwords don't match</p>
                )}

                {/* Terms Agreement */}
                <div className="auth-consent-list">
                  <label className="auth-check-row auth-check-row--top">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className="auth-checkbox"
                      required
                    />
                    <span>
                      I agree to the{' '}
                      <Link to="/terms" className="auth-link">
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="auth-link">
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>

                  <label className="auth-check-row auth-check-row--top">
                    <input
                      type="checkbox"
                      name="subscribeNewsletter"
                      checked={formData.subscribeNewsletter}
                      onChange={handleInputChange}
                      className="auth-checkbox"
                    />
                    <span>
                      Email me LetsBoostUp special offers. I can unsubscribe at any time.
                    </span>
                  </label>
                </div>

                {/* Create Account Button */}
                <button
                  type="submit"
                  disabled={!formData.agreeToTerms || formData.password !== formData.confirmPassword || state.isLoading}
                  className="auth-primary-button"
                >
                  {state.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>

              {/* OR Divider */}
              <div className="auth-divider">
                <span>or</span>
              </div>

              {/* SSO Options */}
              <button
                type="button"
                onClick={handleGoogleSignup}
                disabled={state.isLoading}
                className="auth-secondary-button"
              >
                <Chrome className="w-5 h-5" />
                <span>{state.isLoading ? 'Signing up...' : 'Sign up with Google'}</span>
              </button>

              {/* Sign In link */}
              <p className="auth-switch-copy">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="auth-link auth-link--strong"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
